import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toPng } from "html-to-image";
import { Download, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { disciplineColor } from "@/lib/disciplines";

const TIER_ACCENT = { circle: "text-primary", studio: "text-secondary", house: "text-cream" };

export default function ArtizenIdCard({ embed = false }) {
  const [pass, setPass] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    api.get("/pass/me").then(({ data }) => { setPass(data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const download = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        pixelRatio: 2,
        backgroundColor: "#0a0a0a",
        cacheBust: true,
        // Avoid tainted-canvas: skip cross-origin images instead of crashing
        filter: (node) => !(node.tagName === "IMG" && !node.complete),
      });
      const link = document.createElement("a");
      link.download = `${(pass.pass_code || "artizen-id")}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("ID downloaded");
    } catch (e) {
      toast.error("Could not export · try again");
      console.error(e);
    } finally { setDownloading(false); }
  };

  if (loading) return <p className="font-mono text-sm text-white/60">Loading your Pass…</p>;
  if (!pass?.has_pass) return (
    <div className="border border-border p-6">
      <p className="meta-mono mb-3">— ARTIZEN ID</p>
      <p className="font-display text-2xl font-bold">Get the Pass to <span className="hl-coral">unlock your ID.</span></p>
      <a href="/artizen-pass" className="btn-pill btn-coral mt-4 inline-flex">Join the Pass →</a>
    </div>
  );

  const tierClass = TIER_ACCENT[pass.tier] || "text-primary";
  const memberSince = pass.member_since ? new Date(pass.member_since).toLocaleDateString("en-IN", { month: "short", year: "numeric" }) : "—";
  const discColor = disciplineColor(pass.discipline);

  return (
    <div className={embed ? "" : "border border-border p-5"}>
      {!embed && (
        <div className="flex items-center justify-between mb-4">
          <p className="meta-mono">— YOUR ARTIZEN ID</p>
          <button data-testid="id-download" onClick={download} disabled={downloading} className="btn-pill btn-coral">
            <Download size={13} /> {downloading ? "Rendering…" : "Download PNG"}
          </button>
        </div>
      )}

      {/* The card — fixed aspect for clean export */}
      <div className="overflow-x-auto">
        <div
          ref={cardRef}
          data-testid="artizen-id-card"
          className="relative bg-background grid-bg"
          style={{ width: 1080, height: 680, padding: 56, color: "white", fontFamily: "'Satoshi','General Sans',sans-serif" }}
        >
          {/* Top row */}
          <div className="flex items-start justify-between" style={{ marginBottom: 40 }}>
            <div className="flex items-center" style={{ gap: 14 }}>
              <span style={{ width: 14, height: 14, borderRadius: 999, background: "hsl(5 87% 63%)", display: "inline-block" }} />
              <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 14, letterSpacing: "0.28em", textTransform: "uppercase" }}>anArtizen · Pass</span>
            </div>
            <div style={{ textAlign: "right" }}>
              <p style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 12, letterSpacing: "0.22em", color: "rgba(255,255,255,0.5)", textTransform: "uppercase" }}>MEMBER ID</p>
              <p style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 26, letterSpacing: "0.06em", color: "hsl(5 87% 63%)", marginTop: 6 }}>{pass.pass_code}</p>
            </div>
          </div>

          {/* Middle row: photo + identity */}
          <div className="flex items-end" style={{ gap: 40, marginTop: 30 }}>
            <div style={{ width: 220, height: 280, background: "#191919", border: "1px solid rgba(255,255,255,0.1)", overflow: "hidden", flexShrink: 0 }}>
              {pass.profile_image_url ? (
                <img src={pass.profile_image_url} alt={pass.name} crossOrigin="anonymous" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : (
                <div style={{ width: "100%", height: "100%", display: "grid", placeItems: "center", fontFamily: "'Clash Display',sans-serif", fontWeight: 800, fontSize: 110, color: "rgba(255,255,255,0.2)" }}>{(pass.name || "A")[0]}</div>
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 12, letterSpacing: "0.22em", color: "hsl(5 87% 63%)", textTransform: "uppercase" }}>— {pass.tier_label}</p>
              <p style={{ fontFamily: "'Clash Display','Cabinet Grotesk',sans-serif", fontWeight: 800, fontSize: 78, lineHeight: 0.92, letterSpacing: "-0.025em", marginTop: 14, wordBreak: "break-word" }}>{pass.name}</p>
              <p style={{ fontFamily: "'Instrument Serif',serif", fontStyle: "italic", fontSize: 26, color: "rgba(255,255,255,0.78)", marginTop: 18 }}>
                {pass.aesthetic || (pass.discipline_label && pass.city ? `${pass.discipline_label.toLowerCase()} from ${pass.city}` : "")}
              </p>
            </div>
          </div>

          {/* Bottom row: discipline + city + since */}
          <div style={{ position: "absolute", bottom: 56, left: 56, right: 56, display: "flex", alignItems: "flex-end", justifyContent: "space-between", borderTop: `1px solid ${discColor}33`, paddingTop: 22 }}>
            <div style={{ display: "flex", gap: 36 }}>
              <Cell k="DISCIPLINE" v={pass.discipline_label || "—"} accent={discColor} />
              <Cell k="BASED" v={(pass.city || "—").toUpperCase()} />
              <Cell k="SINCE" v={memberSince.toUpperCase()} />
            </div>
            <div style={{ textAlign: "right" }}>
              <p style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 11, letterSpacing: "0.22em", color: "rgba(255,255,255,0.45)", textTransform: "uppercase" }}>VALID UNTIL CANCELLED</p>
              <p style={{ fontFamily: "'Clash Display',sans-serif", fontWeight: 700, fontSize: 32, marginTop: 6, color: "white" }}>UNFAIR <span style={{ background: discColor, padding: "0 8px", color: needsLightText(discColor) ? "#0a0a0a" : "#ffffff" }}>ADVANTAGE</span></p>
            </div>
          </div>

          {/* Decorative corners */}
          <Corner pos={{ top: 56, left: 56 }} color={discColor} />
          <Corner pos={{ top: 56, right: 56 }} mirror="x" color={discColor} />
          <Corner pos={{ bottom: 56, left: 56 }} mirror="y" color={discColor} />
          <Corner pos={{ bottom: 56, right: 56 }} mirror="xy" color={discColor} />
        </div>
      </div>

      {embed && (
        <div className="mt-4 flex justify-end">
          <button data-testid="id-download" onClick={download} disabled={downloading} className="btn-pill btn-coral">
            <Download size={13} /> {downloading ? "Rendering…" : "Download PNG"}
          </button>
        </div>
      )}
    </div>
  );
}

function Cell({ k, v, accent }) {
  return (
    <div>
      <p style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 11, letterSpacing: "0.22em", color: "rgba(255,255,255,0.5)", textTransform: "uppercase" }}>— {k}</p>
      <p style={{ fontFamily: "'Clash Display',sans-serif", fontWeight: 700, fontSize: 22, marginTop: 6, color: accent || "white", letterSpacing: "-0.01em" }}>{v}</p>
    </div>
  );
}

function Corner({ pos, mirror, color = "hsl(5 87% 63%)" }) {
  const sx = mirror?.includes("x") ? -1 : 1;
  const sy = mirror?.includes("y") ? -1 : 1;
  return (
    <div style={{ position: "absolute", ...pos, width: 18, height: 18, transform: `scale(${sx}, ${sy})` }}>
      <div style={{ position: "absolute", top: 0, left: 0, width: 18, height: 2, background: color }} />
      <div style={{ position: "absolute", top: 0, left: 0, width: 2, height: 18, background: color }} />
    </div>
  );
}

function needsLightText(hex) {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) > 165;
}
